import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { getJugadores, eliminarJugador } from "../services/api";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import "../styles/paneladmin.css";

export default function PanelAdministrador() {
  const { rol, token } = useAuth();
  const navigate = useNavigate();

  const [paginaActual, setPaginaActual] = useState(1);
const jugadoresPorPagina = 10;

  // Declaración de jugadoresFiltrados
  const [jugadoresFiltrados, setJugadoresFiltrados] = useState([]);

  // Cálculo de jugadoresPaginados y totalPaginas
  const indiceUltimoJugador = paginaActual * jugadoresPorPagina;
  const indicePrimerJugador = indiceUltimoJugador - jugadoresPorPagina;
  const jugadoresPaginados = jugadoresFiltrados.slice(indicePrimerJugador, indiceUltimoJugador);
  const totalPaginas = Math.ceil(jugadoresFiltrados.length / jugadoresPorPagina);

  const cambiarPagina = (numeroPagina) => {
    setPaginaActual(numeroPagina);
  };

  const botonesPaginacion = [];
  for (let i = 1; i <= totalPaginas; i++) {
    botonesPaginacion.push(
      <button
        key={i}
        onClick={() => cambiarPagina(i)}
        className={paginaActual === i ? "activo" : ""}
      >
        {i}
      </button>
    );
  }
  
  
  // Datos
  const [jugadores, setJugadores] = useState([]);
  
  // Estados para Filtros
  const [busqueda, setBusqueda] = useState(""); 
  const [filtroCategoria, setFiltroCategoria] = useState(""); // NUEVO: Estado para categoría
  const [filtroGenero, setFiltroGenero] = useState("");       // NUEVO: Estado para género
  
  const [jugadorAEliminar, setJugadorAEliminar] = useState(null);

  // Colores del gráfico
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF'];

  useEffect(() => {
    if (rol === "admin" && token) {
      getJugadores(token).then(res => {
        setJugadores(res.data);
        setJugadoresFiltrados(res.data);
      });
    }
  }, [rol, token]);

  // --- LÓGICA DE FILTRADO AVANZADO ---
  useEffect(() => {
    const filtrados = jugadores.filter(j => {
      // 1. Filtro por Texto (Nombre, Email, DNI)
      const coincideTexto = 
        (j.nombre?.toLowerCase() || "").includes(busqueda.toLowerCase()) ||
        (j.email?.toLowerCase() || "").includes(busqueda.toLowerCase()) ||
        (j.dni?.toString() || "").includes(busqueda);

      // 2. Filtro por Categoría (Si está vacío, los muestra todos)
      const coincideCategoria = filtroCategoria === "" || j.categoria === filtroCategoria;

      // 3. Filtro por Género
      const coincideGenero = filtroGenero === "" || j.genero === filtroGenero;

      // DEBE cumplir las tres condiciones
      return coincideTexto && coincideCategoria && coincideGenero;
    });

    setJugadoresFiltrados(filtrados);
  }, [busqueda, filtroCategoria, filtroGenero, jugadores]);


  // --- EXTRAER OPCIONES PARA LOS SELECTS ---
  // Usamos useMemo para obtener listas únicas de las categorías y géneros que existen en la BD
  const categoriasDisponibles = useMemo(() => {
    return [...new Set(jugadores.map(j => j.categoria).filter(Boolean))].sort();
  }, [jugadores]);

  const generosDisponibles = useMemo(() => {
    return [...new Set(jugadores.map(j => j.genero).filter(Boolean))].sort();
  }, [jugadores]);

  // --- Datos para el Gráfico ---
  const datosGrafico = useMemo(() => {
    const conteo = {};
    jugadores.forEach(j => {
      const cat = j.categoria || "Sin Categoría";
      conteo[cat] = (conteo[cat] || 0) + 1;
    });
    return Object.keys(conteo).map(key => ({ name: key, value: conteo[key] }));
  }, [jugadores]);

  if (rol !== "admin") {
    navigate("/");
    return null;
  }

  // Manejadores
  const handleAgregar = () => navigate("/admin/agregar-jugador");
  const handleEditar = (id) => navigate(`/admin/editar-jugador/${id}`);
  const handleEliminarClick = (id) => setJugadorAEliminar(id);
  const cancelarEliminar = () => setJugadorAEliminar(null);

  const confirmarEliminar = async () => {
    try {
      await eliminarJugador(jugadorAEliminar, token);
      setJugadores(jugadores.filter(j => j.id !== jugadorAEliminar));
      setJugadorAEliminar(null);
    } catch (err) {
      alert("Error al eliminar jugador");
    }
  };

  const getCategoriaBadgeClass = (categoria) => {
    if (!categoria) return "badge-default";
    const cat = categoria.toLowerCase();
    if (cat.includes("primera") || cat.includes("1")) return "badge-primera";
    if (cat.includes("segunda") || cat.includes("2")) return "badge-segunda";
    if (cat.includes("tercera") || cat.includes("3")) return "badge-tercera";
    return "badge-default";
  };

  return (
    <div className="panel-admin-container">
      <h2>Panel de Administrador</h2>
      
      <div className="panel-admin-options">
        <button onClick={handleAgregar}>➕ Agregar Jugador</button>
        <button onClick={() => navigate("/ranking")}>📊 Ver Ranking</button>
        <button onClick={() => navigate("/admin/crear-torneo")}>➕ Crear Torneo</button>
        <button onClick={() => navigate("/admin/lista-torneos")}>🏆 Ver Torneos</button>
      </div>
            {/* --- BARRA DE HERRAMIENTAS DE FILTRO --- */}
      <div className="filtros-container">
        {/* Buscador */}
        <div className="busqueda-wrapper">
          <input
            type="text"
            placeholder="Buscar por nombre, email, DNI..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <span role="img" aria-label="lupa">🔍</span>
        </div>

        {/* Filtro Categoría */}
        <select 
          className="filtro-select"
          value={filtroCategoria}
          onChange={(e) => setFiltroCategoria(e.target.value)}
        >
          <option value="">Todas las Categorías</option>
          {categoriasDisponibles.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        {/* Filtro Género */}
        <select 
          className="filtro-select"
          value={filtroGenero}
          onChange={(e) => setFiltroGenero(e.target.value)}
        >
          <option value="">Todos los Géneros</option>
          {generosDisponibles.map(gen => (
            <option key={gen} value={gen}>{gen}</option>
          ))}
        </select>
        
        {/* Botón para limpiar filtros */}
        {(busqueda || filtroCategoria || filtroGenero) && (
            <button className="btn-limpiar" onClick={() => {
                setBusqueda("");
                setFiltroCategoria("");
                setFiltroGenero("");
            }}>
                Limpiar ✕
            </button>
        )}
      </div>




      
      <h3>Listado de Jugadores</h3>
      <table className="tabla-jugadores">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Categoría</th>
            <th>Puntos</th>
            <th>Género</th>
            <th>Ciudad</th>
            <th>Acciones</th>
          </tr>
        </thead>
<tbody>
  {jugadoresPaginados.length > 0 ? (
    jugadoresPaginados.map((j) => (
      <tr key={j.id}>
        <td>
          <div style={{ fontWeight: "bold" }}>{j.nombre}</div>
          <div style={{ fontSize: "0.85em", color: "#666" }}>{j.email}</div>
        </td>
        <td>
          <span className={`badge ${getCategoriaBadgeClass(j.categoria)}`}>
            {j.categoria}
          </span>
        </td>
        <td style={{ fontWeight: "bold" }}>{j.puntos}</td>
        <td>{j.genero}</td>
        <td>{j.ciudad}</td>
        <td>
          <button onClick={() => handleEditar(j.id)}>Editar</button>
          <button onClick={() => handleEliminarClick(j.id)}>Eliminar</button>
        </td>
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
        No se encontraron jugadores con esos filtros.
      </td>
    </tr>
  )}
</tbody>
      </table>
          <div className="paginacion">
      {botonesPaginacion}
    </div>

      {jugadorAEliminar && (
        <div className="modal">
          <div className="modal-content">
            <p>¿Seguro que quieres eliminar este jugador?</p>
            <button onClick={confirmarEliminar}>Sí, eliminar</button>
            <button onClick={cancelarEliminar}>Cancelar</button>
          </div>
        </div>
      )}

            <div className="dashboard-stats">
        <div className="card-grafico">
          <h3>Distribución por Categorías</h3>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={datosGrafico}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {datosGrafico.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-resumen">
            <h3>Total Jugadores</h3>
            <p className="numero-gigante">{jugadores.length}</p>
            <p>Filtrados: {jugadoresFiltrados.length}</p>
        </div>
      </div>



    </div>

      


  );
}